import {
    type ComponentPropsWithoutRef,
    useEffect,
    useMemo,
    useState,
} from 'react';
import { useTranslation } from 'react-i18next';

import { DateAdapter } from '@myphx-engineering/date-utils/client';
import {
    Chip,
    DatePickerInput,
    Group,
    Stack,
    Subtitle1,
    YearPickerInput,
} from '@myphx-engineering/phx-design-system';

import type {
    GetCoverageMembersQuery,
    TimelineFilter,
} from '../../../graphql/generated/graphql';

import { formatDateToTimelineFilter } from './format-date-to-timeline-filter';
import { getLocalDate } from './get-local-date';

export type CoveragePeriod = 'current' | 'calendarYear' | 'custom';

type YearPickerChangeHandler = NonNullable<
    ComponentPropsWithoutRef<typeof YearPickerInput>['onChange']
>;
type DatePickerChangeHandler = NonNullable<
    ComponentPropsWithoutRef<typeof DatePickerInput>['onChange']
>;
type YearPickerValue = Parameters<YearPickerChangeHandler>[0];

export function CoveragePeriod({
    coveragePeriod,
    onChipSelect,
    onChangeDate,
    currentTimeline,
    data,
}: {
    coveragePeriod: CoveragePeriod;
    onChipSelect: (value: CoveragePeriod) => void;
    onChangeDate: (value: TimelineFilter) => void;
    currentTimeline?: TimelineFilter;
    data: NonNullable<
        GetCoverageMembersQuery['patient']
    >['coveragesConnection']['edges'];
}) {
    const today = useMemo(() => new Date(), []);
    const { t } = useTranslation();

    const { minDate, maxDate } = useMemo(() => {
        let earliest = today;
        let latest = today;

        data.forEach((edge) => {
            if (edge.coverage.startDate) {
                const startDate = getLocalDate(edge.coverage.startDate);
                if (startDate < earliest) {
                    earliest = startDate;
                }
            }
            if (edge.coverage.endDate) {
                const endDate = getLocalDate(edge.coverage.endDate);
                if (endDate > latest) {
                    latest = endDate;
                }
            }
        });

        return {
            minDate: earliest,
            maxDate: latest > today ? today : latest,
        };
    }, [data, today]);

    const [yearValue, setYearValue] = useState<YearPickerValue>(() =>
        currentTimeline
            ? currentTimeline.startDate
            : formatDateToTimelineFilter(today)
    );

    const [dateValue, setDateValue] = useState<[string | null, string | null]>(
        () =>
            currentTimeline
                ? [currentTimeline.startDate, currentTimeline.endDate]
                : [null, null]
    );

    const handleDateChange: DatePickerChangeHandler = (dates) => {
        setDateValue(dates);

        if (dates[0] && dates[1]) {
            const newRange = {
                startDate: dates[0],
                endDate: dates[1],
            };
            onChangeDate(newRange);
        }
        if (coveragePeriod !== 'custom') {
            onChipSelect('custom');
        }
    };

    useEffect(() => {
        if (!coveragePeriod) return;

        if (coveragePeriod !== 'calendarYear' && currentTimeline) {
            const start = currentTimeline.startDate;

            setDateValue([start, formatDateToTimelineFilter(today)]);
            onChangeDate({
                startDate: start,
                endDate: formatDateToTimelineFilter(today),
            });
        } else if (coveragePeriod === 'calendarYear') {
            const yearStart = new Date(today.getFullYear(), 0, 1);
            const yearEnd = new Date(today.getFullYear(), 11, 31);

            const adjustedStart = yearStart < minDate ? minDate : yearStart;
            const adjustedEnd = yearEnd > maxDate ? maxDate : yearEnd;

            setYearValue(formatDateToTimelineFilter(adjustedStart));
            onChangeDate({
                startDate: formatDateToTimelineFilter(adjustedStart),
                endDate: formatDateToTimelineFilter(adjustedEnd),
            });
        }
    }, [
        coveragePeriod,
        currentTimeline,
        today,
        minDate,
        maxDate,
        onChangeDate,
    ]);

    const handleYearChange: YearPickerChangeHandler = (value) => {
        if (!value) return;
        setYearValue(value);

        const year = DateAdapter.plainDateFromString(value).year;
        const yearStart = new Date(year, 0, 1);
        const yearEnd = new Date(year, 11, 31);

        const adjustedStart = yearStart < minDate ? minDate : yearStart;
        const adjustedEnd = yearEnd > maxDate ? maxDate : yearEnd;

        onChangeDate({
            startDate: formatDateToTimelineFilter(adjustedStart),
            endDate: formatDateToTimelineFilter(adjustedEnd),
        });
    };

    const picker =
        coveragePeriod === 'calendarYear' ? (
            <YearPickerInput
                label={t('claimHistory.yearPicker.label')}
                placeholder={t('claimHistory.yearPicker.placeholder')}
                maxDate={maxDate}
                minDate={minDate}
                value={yearValue}
                onChange={handleYearChange}
            />
        ) : (
            <DatePickerInput
                label={t('claimHistory.datePicker.label')}
                placeholder={t('claimHistory.datePicker.placeholder')}
                maxDate={maxDate}
                minDate={minDate}
                value={dateValue}
                onChange={handleDateChange}
                friendlyLabels
            />
        );

    return (
        <Stack>
            <Subtitle1>{t('claimHistory.coveragePeriod.heading')}</Subtitle1>
            <Chip.Group
                onChange={(value) => onChipSelect(value as CoveragePeriod)}
                value={coveragePeriod}
                multiple={false}
            >
                <Group>
                    <Chip value="current">
                        {t('claimHistory.coveragePeriod.current')}
                    </Chip>
                    <Chip value="calendarYear">
                        {t('claimHistory.coveragePeriod.calendarYear')}
                    </Chip>
                    <Chip value="custom">
                        {t('claimHistory.coveragePeriod.custom')}
                    </Chip>
                </Group>
            </Chip.Group>
            {picker}
        </Stack>
    );
}
